import { z } from "zod";
//#region src/channel-grammar.ts
/**
* The ONE canonical channel ROUTING-KEY grammar. Single source of truth for
* every place a channel routing key is validated.
*
* ⛔ A routing key is NOT a display name. The two are different namespaces and
* this file bounds both, separately and deliberately — see
* `channelDisplayNameSchema` below. Applying this pattern to a display name
* rejects the platform's own seeded `Stable`/`Beta`.
*
* Why the grammar is this narrow: the key is materialized verbatim into the
* `x-zion-channel` header by Istio's `outputClaimToHeaders`, and operator-emitted
* HTTPRoutes select an app version by matching that header against
* `activeVersions[].channels`. Gateway-API header matching is case-SENSITIVE and
* offers no normalisation at match time, so producer and consumer must agree on
* an exact byte sequence, and the only sequence both can reach is the canonical
* lowercase one. The failure mode is SILENT: a non-matching key matches no rule,
* the request falls through to the owner version, and returns 200 from the wrong
* code.
*
* It is also the bound that keeps a channel value out of trouble downstream. The
* operator's service-token sidecar builds its mint request body with a shell
* `printf '{"channel":"%s",...}'`, and `printf %s` does not escape JSON — so an
* unbounded value is a JSON-injection primitive that can forge a duplicate
* `orgId` key (issue #804). This grammar admits no `"`, no `\`, and no control
* character, which makes that class unrepresentable at the authoring door.
*
* ⚠️ This bounds the SHAPE, not the vocabulary. A caller may still pass
* `my-channel`, which is canonical and routes nowhere. The routable vocabulary
* is CP's seeded, read-only `release.channels.name` set.
*
* ⛔ Do NOT loosen. Before #804 this pattern existed as six independent
* hand-copies (two mint doors, an inert interceptor constant, the operator's
* route renderer, and — spelled as a shell reject-glob plus a length constant,
* so a grep for the pattern misses it — the operator's service-token sidecar).
* This is the home they now import.
*/
const CHANNEL_KEY_PATTERN = /^[a-z0-9-]{1,32}$/;
/**
* Length ceiling baked into `CHANNEL_KEY_PATTERN`, exported separately because
* the operator's sidecar enforces the rule as a POSIX shell reject-glob plus an
* explicit `${#VAR}` length test and cannot consume a JavaScript RegExp. The 32
* is exact — keep the two in lockstep.
*/
const CHANNEL_KEY_MAX_LENGTH = 32;
/** Human-readable rejection message for a malformed channel routing key. */
const CHANNEL_KEY_MESSAGE = "channel must match ^[a-z0-9-]{1,32}$ (lowercase letters, digits and hyphens)";
/** True iff `value` is a well-formed channel routing key. */
function isValidChannelKey(value) {
	return CHANNEL_KEY_PATTERN.test(value);
}
/** Zod schema for a channel ROUTING KEY. Use at any door authoring one. */
const channelKeySchema = z.string().regex(CHANNEL_KEY_PATTERN, CHANNEL_KEY_MESSAGE);
/**
* The jsonb key under which a machine principal carries its channel in
* `auth.machine_principals.attributes`. Named rather than spelled inline so the
* enroll DTO's bound and the services that read the attribute cannot drift.
*/
const CHANNEL_ATTRIBUTE = "channel";
/**
* Length ceiling for a channel DISPLAY name, matching the
* `release.customer_channels.name` column (`varchar(128)`). Without this bound a
* 129-character name is a database error surfaced as HTTP 500 rather than a 400.
*/
const CHANNEL_DISPLAY_NAME_MAX_LENGTH = 128;
/**
* Control characters are rejected in a display name for two measured reasons,
* neither of them the routing grammar:
*   - `U+0000` cannot be stored in a Postgres `varchar` at all, so a NUL in the
*     name is an unhandled database error rather than a validation failure.
*   - `\n` / `\r` let a display name forge a log line wherever the name is
*     interpolated into a log or a status-reason string.
* Covers C0, DEL and C1.
*/
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001F\u007F-\u009F]/;
/** Human-readable rejection message for a malformed channel display name. */
const CHANNEL_DISPLAY_NAME_MESSAGE = "channel name must be 1-128 characters, not blank, and free of control characters";
/**
* True iff `value` is an acceptable channel DISPLAY name.
*
* ⛔ Deliberately NOT `CHANNEL_KEY_PATTERN`. `release.customer_channels.name` is
* a tenant-authored display string that UIs render: the platform seeds every org
* with `Stable` and `Beta`, dp-app's is `Edge`, and real rows include names with
* spaces. Applying the routing grammar here would reject the platform's own data
* and break a rename round-trip on every existing channel.
*
* The display name is safe to leave broad because it is no longer on the path to
* the mint body — the `channel` claim is minted from the closed `ringName` union,
* not from this column. This bound closes the two defects that remain: an
* unbounded length (a 500 instead of a 400) and control characters.
*/
function isValidChannelDisplayName(value) {
	return value.trim().length > 0 && value.length <= 128 && !CONTROL_CHARACTER_PATTERN.test(value);
}
/** Zod schema for a channel DISPLAY name. */
const channelDisplayNameSchema = z.string().refine(isValidChannelDisplayName, { message: CHANNEL_DISPLAY_NAME_MESSAGE });
/**
* The routing-key grammar of a PRIVATE release channel: `p-` and twelve lowercase hex digits.
*
* A private channel (`release.channels` row of kind `private`, private-release-channels L-1)
* routes on a key the platform derives from the row id, never on its path-style slug. The slug
* carries `/`, which {@link CHANNEL_KEY_PATTERN} refuses and which the operator's sidecar glob and
* the `x-zion-channel` header cannot carry (L-2). `privateChannelKeyFor` in `release-channels.ts`
* is the one producer of a value matching this pattern.
*
* Every match is also a {@link CHANNEL_KEY_PATTERN} match (14 characters, lowercase hex and one
* hyphen), so no enforcement site of the routing grammar changes. The four public ring names never
* match it, which is what lets a reader tell a private key from a ring name by shape alone.
*/
const PRIVATE_CHANNEL_KEY_PATTERN = /^p-[0-9a-f]{12}$/;
/** True iff `value` is a well-formed private-channel routing key (`p-` and 12 lowercase hex digits). */
function isPrivateChannelKey(value) {
	return PRIVATE_CHANNEL_KEY_PATTERN.test(value);
}
//#endregion
export { CHANNEL_ATTRIBUTE, CHANNEL_DISPLAY_NAME_MAX_LENGTH, CHANNEL_DISPLAY_NAME_MESSAGE, CHANNEL_KEY_MAX_LENGTH, CHANNEL_KEY_MESSAGE, CHANNEL_KEY_PATTERN, PRIVATE_CHANNEL_KEY_PATTERN, channelDisplayNameSchema, channelKeySchema, isPrivateChannelKey, isValidChannelDisplayName, isValidChannelKey };

//# sourceMappingURL=channel-grammar.js.map